import React, {ChangeEvent, useEffect, useState} from 'react';
import {Box, TextField, Button, Paper, Typography} from '@mui/material';
import {useAppDispatch} from '../../../app/hooks';
import {closeButton, closeButtonInner, form, formInner, overlay} from "../../Players/components/playerFormStyles";
import CloseIcon from "@mui/icons-material/Close";
import {Controller, useForm} from "react-hook-form";
import {
    createAdvertiser,
    IAdvertiser,
    ICreateAdvertiserPayload,
    IUpdateAdvertiserPayload,
    updateAdvertiser
} from "../advertisersSlice";
import {isValidData} from "../../../helpers/isValidData";
import {toast} from "react-toastify";


interface IAdvertiserFormProps {
    editingAdvertiser: IAdvertiser | null,
    onCloseForm: () => void
}

export enum advertiserFormFields {
    NAME = "name",
    FACEBOOK = "facebook",
    LINKEDIN = "linkedIn",
    COMMENTS = "comments",
    HOME_TOWN = "hometown",
    COUNTRY = "country",
    TELEPHONE = "telephone",
    EMAIL = "email",
    WEBSITE = "webSite",
}

interface IAdvertiserFormData {
    name: string
    facebook: string,
    linkedIn: string,
    comments: string
    hometown: string,
    country: string,
    telephone: string,
    email: string,
    webSite: string
}


const AdvertiserForm: React.FC<IAdvertiserFormProps> = ({editingAdvertiser, onCloseForm}) => {
    const [formData, setFormData] = useState<IAdvertiserFormData>({
        name: "",
        facebook: "",
        linkedIn: "",
        comments: "",
        hometown: "",
        country: "",
        telephone: "",
        email: "",
        webSite: ""
    });
    const {handleSubmit, control, reset} = useForm<IAdvertiserFormData>();
    const dispatch = useAppDispatch();

    useEffect(() => {
        if (editingAdvertiser) {
            const userFormData = {
                name: editingAdvertiser ? editingAdvertiser.name : "",
                facebook: editingAdvertiser.socialMedia ? editingAdvertiser.socialMedia.facebook : "",
                linkedIn: editingAdvertiser.socialMedia ? editingAdvertiser.socialMedia.linkedIn : "",
                comments: editingAdvertiser.socialMedia ? editingAdvertiser.socialMedia.comments : "",
                hometown: editingAdvertiser.contact ? editingAdvertiser.contact.hometown : "",
                country: editingAdvertiser.contact ? editingAdvertiser.contact.country : "",
                telephone: editingAdvertiser.contact ? editingAdvertiser.contact.telephon : "",
                email: editingAdvertiser.contact ? editingAdvertiser.contact.email : "",
                webSite: editingAdvertiser.contact ? editingAdvertiser.contact.webSite : "",
            }
            setFormData(userFormData);
            reset(userFormData);
        }

    }, [editingAdvertiser])


    const handleChange = (field: keyof IAdvertiserFormData, e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFormData({...formData, [field]: e.target.value});
    };


    const onSubmit = () => {
        if (!isValidData(formData)) {
            toast.warning("SDC: Fill the form")
            return;
        }
        if (!editingAdvertiser) {
            const reqBody: ICreateAdvertiserPayload = {
                payload: {
                    name: formData.name,
                    socialMedia: {
                        facebook: formData.facebook,
                        linkedIn: formData.linkedIn,
                        comments: formData.comments
                    },
                    contact: {
                        hometown: formData.hometown,
                        country: formData.country,
                        telephon: formData.telephone,
                        email: formData.email,
                        webSite: formData.webSite
                    }
                },
                closeForm: onCloseForm
            }

            dispatch(createAdvertiser(reqBody));
        } else {
            const reqBody: IUpdateAdvertiserPayload = {
                payload: {
                    id: editingAdvertiser.id,
                    socialMedia: {
                        facebook: formData.facebook,
                        linkedIn: formData.linkedIn,
                        comments: formData.comments
                    },
                    contact: {
                        hometown: formData.hometown,
                        country: formData.country,
                        telephon: formData.telephone,
                        email: formData.email,
                        webSite: formData.webSite
                    }
                },
                closeForm: onCloseForm
            }

            dispatch(updateAdvertiser(reqBody));
        }
    };

    return (
        <Box sx={overlay}>
            <Box>
                <Paper sx={formInner}>
                    <Box sx={closeButtonInner}>
                        <Button
                            sx={closeButton}
                            onClick={onCloseForm}
                        >
                            <CloseIcon sx={{width: 20}}/>
                        </Button>
                    </Box>
                    <Typography variant="h5" align="center">
                        {editingAdvertiser ? "Edit Advertiser" : "Create Advertiser"}
                    </Typography>
                    <Box
                        component="form"
                        onSubmit={handleSubmit(onSubmit)}
                    >
                        <Box sx={form}>
                            <Controller
                                control={control}
                                name={advertiserFormFields.NAME}
                                rules={{
                                    minLength: 2,
                                    maxLength: 150,
                                    pattern: {
                                        value: /[^\s]/,
                                        message: "Field can not contain only spaces"
                                    }
                                }}
                                render={({
                                             field: {onChange},
                                             formState: {errors},
                                         }) => (
                                    <TextField
                                        label="Name"
                                        value={formData.name}
                                        onChange={(e) => {
                                            onChange(e)
                                            handleChange(advertiserFormFields.NAME, e)
                                        }}
                                        error={Boolean(errors.facebook)}
                                        helperText={
                                            errors.facebook && errors.facebook.type === "maxLength"
                                            && <span>This field can not contain more than 150 symbols</span>
                                            ||
                                            errors.facebook && errors.facebook.type === "minLength"
                                            && <span>This field can not contain less than 2 symbols</span>
                                            ||
                                            errors.facebook && errors.facebook.type === "pattern"
                                            && <span>Field can not contain only spaces</span>
                                        }
                                    />
                                )}
                            />
                            <Controller
                                control={control}
                                name={advertiserFormFields.FACEBOOK}
                                rules={{
                                    minLength: 2,
                                    maxLength: 150,
                                    pattern: {
                                        value: /[^\s]/,
                                        message: "Field can not contain only spaces"
                                    }
                                }}
                                render={({
                                             field: {onChange},
                                             formState: {errors},
                                         }) => (
                                    <TextField
                                        label="Facebook"
                                        value={formData.facebook}
                                        onChange={(e) => {
                                            onChange(e)
                                            handleChange(advertiserFormFields.FACEBOOK, e)
                                        }}
                                        error={Boolean(errors.facebook)}
                                        helperText={
                                            errors.facebook && errors.facebook.type === "maxLength"
                                            && <span>This field can not contain more than 150 symbols</span>
                                            ||
                                            errors.facebook && errors.facebook.type === "minLength"
                                            && <span>This field can not contain less than 2 symbols</span>
                                            ||
                                            errors.facebook && errors.facebook.type === "pattern"
                                            && <span>Field can not contain only spaces</span>
                                        }
                                    />
                                )}
                            />
                            <Controller
                                control={control}
                                name={advertiserFormFields.LINKEDIN}
                                rules={{
                                    minLength: 2,
                                    maxLength: 150,
                                    pattern: {
                                        value: /[^\s]/,
                                        message: "Field can not contain only spaces"
                                    }
                                }}
                                render={({
                                             field: {onChange},
                                             formState: {errors},
                                         }) => (
                                    <TextField
                                        label="LinkedIn"
                                        value={formData.linkedIn}
                                        onChange={(e) => {
                                            onChange(e)
                                            handleChange(advertiserFormFields.LINKEDIN, e)
                                        }}
                                        error={Boolean(errors.linkedIn)}
                                        helperText={
                                            errors.linkedIn && errors.linkedIn.type === "maxLength"
                                            && <span>This field can not contain more than 150 symbols</span>
                                            ||
                                            errors.linkedIn && errors.linkedIn.type === "minLength"
                                            && <span>This field can not contain less than 2 symbols</span>
                                            ||
                                            errors.linkedIn && errors.linkedIn.type === "pattern"
                                            && <span>Field can not contain only spaces</span>
                                        }
                                    />
                                )}
                            />
                            <Controller
                                control={control}
                                name={advertiserFormFields.COMMENTS}
                                rules={{
                                    minLength: 2,
                                    maxLength: 150,
                                    pattern: {
                                        value: /[^\s]/,
                                        message: "Field can not contain only spaces"
                                    }
                                }}
                                render={({
                                             field: {onChange},
                                             formState: {errors},
                                         }) => (
                                    <TextField
                                        label="Comments"
                                        value={formData.comments}
                                        onChange={(e) => {
                                            onChange(e)
                                            handleChange(advertiserFormFields.COMMENTS, e)
                                        }}
                                        error={Boolean(errors.comments)}
                                        helperText={
                                            errors.comments && errors.comments.type === "maxLength"
                                            && <span>This field can not contain more than 150 symbols</span>
                                            ||
                                            errors.comments && errors.comments.type === "minLength"
                                            && <span>This field can not contain less than 2 symbols</span>
                                            ||
                                            errors.comments && errors.comments.type === "pattern"
                                            && <span>Field can not contain only spaces</span>
                                        }
                                    />
                                )}
                            />
                            <Controller
                                control={control}
                                name={advertiserFormFields.COUNTRY}
                                rules={{
                                    minLength: 2,
                                    maxLength: 150,
                                    pattern: {
                                        value: /[^\s]/,
                                        message: "Field can not contain only spaces"
                                    }
                                }}
                                render={({
                                             field: {onChange},
                                             formState: {errors},
                                         }) => (
                                    <TextField
                                        label="Country"
                                        value={formData.country}
                                        onChange={(e) => {
                                            onChange(e)
                                            handleChange(advertiserFormFields.COUNTRY, e)
                                        }}
                                        error={Boolean(errors.country)}
                                        helperText={
                                            errors.country && errors.country.type === "maxLength"
                                            && <span>This field can not contain more than 150 symbols</span>
                                            ||
                                            errors.country && errors.country.type === "minLength"
                                            && <span>This field can not contain less than 2 symbols</span>
                                            ||
                                            errors.country && errors.country.type === "pattern"
                                            && <span>Field can not contain only spaces</span>
                                        }
                                    />
                                )}
                            />
                            <Controller
                                control={control}
                                name={advertiserFormFields.HOME_TOWN}
                                rules={{
                                    minLength: 2,
                                    maxLength: 150,
                                    pattern: {
                                        value: /[^\s]/,
                                        message: "Field can not contain only spaces"
                                    }
                                }}
                                render={({
                                             field: {onChange},
                                             formState: {errors},
                                         }) => (
                                    <TextField
                                        label="Home town"
                                        value={formData.hometown}
                                        onChange={(e) => {
                                            onChange(e)
                                            handleChange(advertiserFormFields.HOME_TOWN, e)
                                        }}
                                        error={Boolean(errors.hometown)}
                                        helperText={
                                            errors.hometown && errors.hometown.type === "maxLength"
                                            && <span>This field can not contain more than 150 symbols</span>
                                            ||
                                            errors.hometown && errors.hometown.type === "minLength"
                                            && <span>This field can not contain less than 2 symbols</span>
                                            ||
                                            errors.hometown && errors.hometown.type === "pattern"
                                            && <span>Field can not contain only spaces</span>
                                        }
                                    />
                                )}
                            />
                            <Controller
                                control={control}
                                name={advertiserFormFields.TELEPHONE}
                                rules={{
                                    minLength: 2,
                                    maxLength: 150,
                                    pattern: {
                                        value: /[^\s]/,
                                        message: "Field can not contain only spaces"
                                    }
                                }}
                                render={({
                                             field: {onChange},
                                             formState: {errors},
                                         }) => (
                                    <TextField
                                        label="Telephone"
                                        value={formData.telephone}
                                        onChange={(e) => {
                                            onChange(e)
                                            handleChange(advertiserFormFields.TELEPHONE, e)
                                        }}
                                        error={Boolean(errors.telephone)}
                                        helperText={
                                            errors.telephone && errors.telephone.type === "maxLength"
                                            && <span>This field can not contain more than 150 symbols</span>
                                            ||
                                            errors.telephone && errors.telephone.type === "minLength"
                                            && <span>This field can not contain less than 2 symbols</span>
                                            ||
                                            errors.telephone && errors.telephone.type === "pattern"
                                            && <span>Field can not contain only spaces</span>
                                        }
                                    />
                                )}
                            />
                            <Controller
                                control={control}
                                name={advertiserFormFields.WEBSITE}
                                rules={{
                                    minLength: 2,
                                    maxLength: 150,
                                    pattern: {
                                        value: /[^\s]/,
                                        message: "Field can not contain only spaces"
                                    }
                                }}
                                render={({
                                             field: {onChange},
                                             formState: {errors},
                                         }) => (
                                    <TextField
                                        label="Website"
                                        value={formData.webSite}
                                        onChange={(e) => {
                                            onChange(e)
                                            handleChange(advertiserFormFields.WEBSITE, e)
                                        }}
                                        error={Boolean(errors.webSite)}
                                        helperText={
                                            errors.webSite && errors.webSite.type === "maxLength"
                                            && <span>This field can not contain more than 150 symbols</span>
                                            ||
                                            errors.webSite && errors.webSite.type === "minLength"
                                            && <span>This field can not contain less than 2 symbols</span>
                                            ||
                                            errors.webSite && errors.webSite.type === "pattern"
                                            && <span>Field can not contain only spaces</span>
                                        }
                                    />
                                )}
                            />
                            <Controller
                                control={control}
                                name={advertiserFormFields.EMAIL}
                                rules={{
                                    minLength: 2,
                                    maxLength: 150,
                                    pattern: {
                                        value: /[^\s]/,
                                        message: "Field can not contain only spaces"
                                    }
                                }}
                                render={({
                                             field: {onChange},
                                             formState: {errors},
                                         }) => (
                                    <TextField
                                        label="Email"
                                        value={formData.email}
                                        onChange={(e) => {
                                            onChange(e)
                                            handleChange(advertiserFormFields.EMAIL, e)
                                        }}
                                        error={Boolean(errors.email)}
                                        helperText={
                                            errors.email && errors.email.type === "maxLength"
                                            && <span>This field can not contain more than 150 symbols</span>
                                            ||
                                            errors.email && errors.email.type === "minLength"
                                            && <span>This field can not contain less than 2 symbols</span>
                                            ||
                                            errors.email && errors.email.type === "pattern"
                                            && <span>Field can not contain only spaces</span>
                                        }
                                    />
                                )}
                            />
                            <Button variant="contained" type="submit">
                                Submit
                            </Button>
                        </Box>
                    </Box>
                </Paper>
            </Box>
        </Box>
    );
};

export default AdvertiserForm;
